import React, { useCallback, useEffect, useState } from 'react';
import { Box, Tabs, Tab, IconButton, Tooltip } from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { VehiclePictureTab } from 'types/vehicles/types';
import { useTabOperations } from '../hooks/useTabOperations';
import { TabManagementDialog } from './TabManagementDialog';
import { DeleteConfirmDialog } from './DeleteConfirmDialog';
import { ErrorResponse } from 'types/general/types';
import { PictureToDisplay } from '../utils/pictureUtils';

interface TabsManagerProps {
  vehicleId?: string;
  addingNewVehicle: boolean;
  canEdit: boolean;
  tabs: VehiclePictureTab[];
  selectedTabId: string;
  allDisplayPictures: PictureToDisplay[];
  onTabChange: (tabId: string) => void;
  onPageChange: (page: number) => void;
  onError?: (error: ErrorResponse | null) => void;
  setIsLoading?: (loading: boolean) => void;
  onSuccess?: () => void;
}

export const TabsManager: React.FC<TabsManagerProps> = ({
  vehicleId,
  addingNewVehicle,
  canEdit,
  tabs,
  selectedTabId,
  allDisplayPictures,
  onTabChange,
  onPageChange,
  onError,
  setIsLoading,
  onSuccess,
}) => {
  const [tabDialogOpen, setTabDialogOpen] = useState(false);
  const [tabDialogMode, setTabDialogMode] = useState<'create' | 'edit'>('create');
  const [editingTab, setEditingTab] = useState<VehiclePictureTab | undefined>(undefined);
  const [showTabDeleteConfirmation, setShowTabDeleteConfirmation] = useState(false);
  const [deletingTabId, setDeletingTabId] = useState<string | null>(null);
  const [pendingTabSelection, setPendingTabSelection] = useState<string | null>(null);
  const [deletedTabIndex, setDeletedTabIndex] = useState<number | null>(null);

  const tabHasPictures = useCallback(
    (tabId: string) => {
      return allDisplayPictures.some(picture => picture.tab === tabId);
    },
    [allDisplayPictures]
  );

  const handleCreateTab = useCallback(() => {
    setTabDialogMode('create');
    setEditingTab(undefined);
    setTabDialogOpen(true);
  }, []);

  const handleEditTab = useCallback((tab: VehiclePictureTab, event: React.MouseEvent) => {
    event.stopPropagation();
    setTabDialogMode('edit');
    setEditingTab(tab);
    setTabDialogOpen(true);
  }, []);

  const handleDeleteTabClick = useCallback((tabId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setDeletingTabId(tabId);
    setShowTabDeleteConfirmation(true);
  }, []);

  const handleTabsSuccessUpdate = useCallback(() => {
    onSuccess?.();
  }, [onSuccess]);

  // After new tabs are loaded, check if we should select a pending tab or handle deleted tab
  useEffect(() => {
    if (pendingTabSelection && tabs.find(t => t.id === pendingTabSelection)) {
      onTabChange(pendingTabSelection);
      onPageChange(1);
      setPendingTabSelection(null);
    } else if (deletedTabIndex !== null) {
      // After deletion, select the previous tab (or first tab if we deleted the first one)
      const newSelectedIndex = Math.max(0, deletedTabIndex - 1);
      if (tabs[newSelectedIndex]) {
        onTabChange(tabs[newSelectedIndex].id);
        onPageChange(1);
      }
      setDeletedTabIndex(null);
    }
  }, [tabs, pendingTabSelection, deletedTabIndex, onTabChange, onPageChange]);

  const { createTab, updateTab, deleteTab } = useTabOperations({
    vehicleId,
    onError,
    setIsLoading,
    onSuccess: handleTabsSuccessUpdate,
  });

  const handleTabSubmit = useCallback(
    async (name: string, visibility: string[]) => {
      if (tabDialogMode === 'create') {
        const createdTab = await createTab(name, visibility);
        // If the backend returns the created tab with an ID, mark it for selection
        // The selection will happen after the fresh data is fetched and synced
        if (createdTab?.id) {
          setPendingTabSelection(createdTab.id);
        }
      } else if (tabDialogMode === 'edit' && editingTab) {
        updateTab(editingTab.id, name, visibility);
      }
    },
    [tabDialogMode, editingTab, createTab, updateTab, setPendingTabSelection]
  );

  const handleDeleteTabConfirm = useCallback(() => {
    if (deletingTabId) {
      // Find the index of the tab being deleted
      const deletingIndex = tabs.findIndex(t => t.id === deletingTabId);
      if (deletingIndex !== -1) {
        setDeletedTabIndex(deletingIndex);
      }
      deleteTab(deletingTabId);
    }
    setShowTabDeleteConfirmation(false);
    setDeletingTabId(null);
  }, [deletingTabId, deleteTab, tabs]);

  const handleInternalTabChange = useCallback(
    (tabId: string) => {
      // Ignore the "add" tab button click
      if (tabId === 'add') {
        return;
      }
      onTabChange(tabId);
      onPageChange(1);
    },
    [onTabChange, onPageChange]
  );

  return (
    <>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
        <Tabs
          value={tabs.some(t => t.id === selectedTabId) ? selectedTabId : false}
          onChange={(_, newValue) => handleInternalTabChange(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {tabs.map(tab => {
            const hasPictures = tabHasPictures(tab.id);

            return (
              <Tab
                key={tab.id}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>{tab.name}</span>
                    {/* eslint-disable-next-line camelcase */}
                    {!tab.is_default && !addingNewVehicle && canEdit && selectedTabId === tab.id ? (
                      <Box sx={{ display: 'flex', gap: 0.5, ml: 0.5 }}>
                        <Tooltip title="Edit tab">
                          <IconButton
                            component="span"
                            size="small"
                            onClick={e => handleEditTab(tab, e)}
                            sx={{ padding: '2px' }}
                          >
                            <EditIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={hasPictures ? 'Cannot delete tab with pictures' : 'Delete tab'}>
                          <IconButton
                            component="span"
                            size="small"
                            onClick={e => {
                              if (!hasPictures) handleDeleteTabClick(tab.id, e);
                            }}
                            sx={{
                              padding: '2px',
                              ...(hasPictures && { opacity: 0.38, cursor: 'default' }),
                            }}
                          >
                            <DeleteIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ) : null}
                  </Box>
                }
                value={tab.id}
              />
            );
          })}

          {/* Add new tab button */}
          {!addingNewVehicle && canEdit ? (
            <Tab icon={<AddIcon />} value="add" onClick={handleCreateTab} sx={{ minWidth: 'auto', px: 2 }} />
          ) : null}
        </Tabs>
      </Box>

      {/* Tab Management Dialog */}
      <TabManagementDialog
        open={tabDialogOpen}
        mode={tabDialogMode}
        existingTab={editingTab}
        onClose={() => setTabDialogOpen(false)}
        onSubmit={handleTabSubmit}
      />

      {/* Delete Tab Confirmation Dialog */}
      <DeleteConfirmDialog
        open={showTabDeleteConfirmation}
        title="Delete Tab"
        message={`Are you sure you want to delete the "${tabs.find(t => t.id === deletingTabId)?.name}" tab?`}
        onClose={() => {
          setShowTabDeleteConfirmation(false);
          setDeletingTabId(null);
        }}
        onConfirm={handleDeleteTabConfirm}
      />
    </>
  );
};
